import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PendingCard from "../components/PendingCard";
import { useToast } from "../contexts/ToastContext";
import { api } from "../lib/api";
import { usePendingMutation } from "../lib/hooks/usePendingMutation";
import { MeOut, PendingOut } from "../lib/schemas";
import { useBackButton } from "../lib/telegram";
import { Skeleton } from "../ui";

export default function Inbox() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const chatParam = searchParams.get("chat");
  const [filter, setFilter] = useState<string | null>(null);
  const [filterInitialized, setFilterInitialized] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const showToast = useToast();
  const mutation = usePendingMutation();

  useBackButton(() => {
    if (chatParam) nav(`/groups/${chatParam}`);
    else nav("/");
  });

  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: () => api<MeOut>("GET", "/me"),
    refetchInterval: 60_000,
  });

  const { data: pending, error: pendingError } = useQuery({
    queryKey: ["pending"],
    queryFn: () => api<PendingOut[]>("GET", "/pending"),
    refetchInterval: 5_000,
    staleTime: 5_000,
  });

  useEffect(() => {
    if (filterInitialized || !pending || !chatParam) return;
    const chatId = Number(chatParam);
    const match = pending.find((p) => p.chat.id === chatId);
    if (match) {
      setFilter(match.chat.title);
      setFilterInitialized(true);
    }
  }, [pending, chatParam, filterInitialized]);

  async function handleBulkApprove(items: PendingOut[]) {
    setBulkApproving(true);
    try {
      await Promise.all(
        items.map((p) => mutation.mutateAsync({ token: p.token, action: "approve" })),
      );
      showToast(`Approved ${items.length} items`, { type: "success" });
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Bulk approve failed", { type: "error" });
    } finally {
      setBulkApproving(false);
    }
  }

  const chatTitles = pending
    ? [...new Set(pending.map((p) => p.chat.title))]
    : [];

  const filtered =
    pending && filter ? pending.filter((p) => p.chat.title === filter) : pending;

  const approvable =
    me && filtered
      ? filtered.filter(
          (p) => p.sender_tg_id !== me.user.telegram_id && p.action_type !== "receipt_settle"
        )
      : [];

  const bySender = approvable.reduce<Record<number, PendingOut[]>>((acc, p) => {
    if (!acc[p.sender_tg_id]) acc[p.sender_tg_id] = [];
    acc[p.sender_tg_id].push(p);
    return acc;
  }, {});

  const bulkSenders = Object.entries(bySender)
    .filter(([, items]) => items.length >= 3)
    .map(([senderId, items]) => ({ senderId: Number(senderId), items }));

  if (pendingError && !pending) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4">
        <p className="text-danger">
          {pendingError instanceof Error ? pendingError.message : "Failed to load"}
        </p>
      </div>
    );
  }

  if (!pending || !me) {
    return (
      <div className="min-h-screen bg-bg px-4 pt-6 pb-8">
        <Skeleton className="h-4 w-44 mb-4" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-surface border border-hairline rounded-2xl shadow-card p-4 mb-3">
            <Skeleton className="h-4 w-3/4 mb-3" />
            <Skeleton className="h-3 w-1/2 mb-4" />
            <div className="flex gap-2">
              <Skeleton className="h-9 w-20" />
              <Skeleton className="h-9 w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg px-4 pt-6 pb-8">
      <h1 className="font-sans text-xl font-bold text-fg mb-4">
        Pending approvals
      </h1>

      {/* Filter tags */}
      {chatTitles.length > 1 && (
        <div className="flex gap-2 flex-wrap mb-4">
          <button
            type="button"
            onClick={() => setFilter(null)}
            className={`px-3.5 py-2 rounded-lg font-sans text-[13px] font-semibold border transition-colors ${
              filter === null
                ? "border-fg bg-fg text-bg"
                : "border-hairline text-muted active:bg-surface"
            }`}
          >
            All
          </button>
          {chatTitles.map((title) => (
            <button
              key={title}
              type="button"
              onClick={() => setFilter(title)}
              className={`px-3.5 py-2 rounded-lg font-sans text-[13px] font-semibold border transition-colors ${
                filter === title
                  ? "border-fg bg-fg text-bg"
                  : "border-hairline text-muted active:bg-surface"
              }`}
            >
              {title}
            </button>
          ))}
        </div>
      )}

      {/* Bulk approve buttons */}
      {bulkSenders.map(({ senderId, items }) => (
        <button
          key={senderId}
          type="button"
          disabled={bulkApproving}
          onClick={() => void handleBulkApprove(items)}
          className="w-full bg-fg text-bg px-4 py-3 mb-3 rounded-xl font-sans text-sm font-semibold active:opacity-70 transition-opacity disabled:opacity-50"
        >
          {bulkApproving ? "Approving…" : `Approve all (${items.length})`}
        </button>
      ))}

      {/* Pending cards */}
      {filtered && filtered.length === 0 ? (
        <p className="text-muted text-center py-8">Nothing pending.</p>
      ) : (
        filtered?.map((p) => (
          <PendingCard
            key={p.token}
            pending={p}
            currentTgId={me.user.telegram_id}
          />
        ))
      )}
    </div>
  );
}
